import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { TelemonitoringRequestComponent } from '@modules/telemonitoring/telemonitoring-request/telemonitoring-request.component';
import { Store } from '@ngrx/store';
import { CreatorType } from '@pixacare/domain';
import { FlattenedSequence } from '@pixacare/dtos';
import { AuthorizationsService } from '@services/authorizations.service';
import { DialogService } from '@services/dialog.service';
import { UserHttpService } from '@services/http/user.http.service';
import { MessageWithReceivers } from '@shared/models/chat/message-with-receivers';
import { SequenceContext } from '@shared/models/enums/sequence-context.enum';
import { Status } from '@shared/models/enums/status.enum';
import { ActionsService } from '@shared/models/helpers/actions-service';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { selectClients } from '@shared/store/clients/clients.selectors';
import { selectUserEnabledClientDepartmentIds } from '@shared/store/departments/departments.selectors';
import { selectDepartmentId } from '@shared/store/router/router.selectors';
import { sequencesActions } from '@shared/store/sequences/sequences.actions';
import { telemonitoringsActions } from '@shared/store/telemonitorings/telemonitorings.actions';
import { selectTelemonitoring } from '@shared/store/telemonitorings/telemonitorings.selectors';
import { isEverythingTrue$ } from '@shared/utils/utils';
import { first, map, Observable, of, switchMap } from 'rxjs';
import { SequenceCreateInputs } from '../sequence-create/sequence-create-inputs';
import { SequenceCreateComponent } from '../sequence-create/sequence-create.component';
import { SequenceEditComponent } from '../sequence-edit/sequence-edit.component';
import { SequenceMoveComponent } from '../sequence-move/sequence-move.component';
import { SequenceShareComponent } from '../sequence-share/sequence-share.component';

@Injectable({
  providedIn: 'root',
})
export class SequenceActionsService extends ActionsService {
  private authorizationsService = inject(AuthorizationsService);
  store = inject(Store);
  private router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly translocoService = inject(TranslocoService);
  private readonly userService = inject(UserHttpService);

  actions: MenuAction<FlattenedSequence>[] = [
    {
      id: MenuActionId.SEQUENCE_EDIT,
      label: this.translocoService.translate('Modifier'),
      icon: '@tui.pencil',
      condition: (sequence: FlattenedSequence): boolean =>
        this.isPatientAnonymous(sequence) || this.isSequenceActive(sequence),
      execute: (sequence: FlattenedSequence): void => {
        this.dialogService
          .openComponent(SequenceEditComponent, {
            label: this.translocoService.translate('Modifier la séquence'),
            size: 'l',
            dismissible: this.dialogService.exitConfirmDialog,
            closable: this.dialogService.exitConfirmDialog,
            data: {
              sequenceId: sequence.sequenceInstance.id,
              clientCode: sequence.clientCode,
            },
          })
          .subscribe();
      },
    },
    {
      id: MenuActionId.SEQUENCE_SHARE,
      label: this.translocoService.translate('Partager'),
      icon: '@tui.external-link',
      condition$: (sequence: FlattenedSequence): Observable<boolean> =>
        isEverythingTrue$(
          this.isPatientAnonymous(sequence) || this.isSequenceActive(sequence),
          this.isUserAdminOrOwner$(sequence),
        ),
      execute: (sequence: FlattenedSequence): void => {
        this.dialogService
          .openComponent(SequenceShareComponent, {
            label: this.translocoService.translate('Partager la séquence'),
            size: 'l',
            appearance: 'fullscreen',
            data: {
              sequence,
            },
          })
          .pipe(first())
          .subscribe((newMessage: MessageWithReceivers | null) => {
            if (!newMessage) {
              return;
            }
            if (newMessage.receivers.toUserIds.length === 1) {
              this.userService
                .getUser(newMessage.receivers.toUserIds[0])
                .pipe(first())
                .subscribe((user) => {
                  this.router.navigate(['/chat'], {
                    queryParams: {
                      userId: user.id,
                      firstName: user.firstName,
                      lastName: user.lastName,
                    },
                    queryParamsHandling: 'merge',
                  });
                });
            } else if (newMessage.receivers.toChannelIds.length === 1) {
              this.router.navigate(['/chat', newMessage.receivers.toChannelIds[0]], {
                queryParamsHandling: 'merge',
              });
            } else if (newMessage.receivers.toUserIds.length > 0 || newMessage.receivers.toChannelIds.length > 0) {
              this.router.navigate(['/chat'], { queryParamsHandling: 'merge' });
            }
          });
      },
    },
    {
      id: MenuActionId.SEQUENCE_MOVE,
      label: this.translocoService.translate('Déplacer'),
      icon: '@tui.shuffle',
      condition$: (sequence: FlattenedSequence): Observable<boolean> =>
        isEverythingTrue$(
          this.isPatientAnonymous(sequence) || this.isSequenceActive(sequence),
          this.isUserAdminOrOwner$(sequence),
          this.hasMultipleClients$(),
        ),
      execute: (sequence: FlattenedSequence): void => {
        this.dialogService
          .openComponent<SequenceMoveComponent, string>(SequenceMoveComponent, {
            label: this.translocoService.translate('Déplacer la séquence'),
            data: {
              flattenedSequence: sequence,
            },
          })
          .subscribe((result) => {
            if (result) {
              this.store.dispatch(
                sequencesActions.moveSequence({
                  fromClientCode: sequence.clientCode,
                  toClientCode: result,
                  overridePatient: false,
                  sequenceId: sequence.sequenceInstance.id,
                }),
              );
            }
          });
      },
    },
    {
      id: MenuActionId.SEQUENCE_TELEMONITORING,
      label: this.translocoService.translate('Télésuivi'),
      icon: 'telemonitoring',
      context: SequenceContext.SEQUENCE,
      condition: (sequence: FlattenedSequence): boolean =>
        !this.isPatientAnonymous(sequence) && this.isSequenceActive(sequence),
      execute: (sequence: FlattenedSequence): void => {
        this.store
          .select(selectDepartmentId)
          .pipe(first())
          .subscribe((departmentId) => {
            this.dialogService
              .openComponent(TelemonitoringRequestComponent, {
                label: this.translocoService.translate('Initier un télésuivi'),
                size: 'l',
                appearance: 'fullscreen',
                data: {
                  patient: sequence.patientInstance,
                  clientCode: sequence.clientCode,
                  departmentIds: departmentId ? [departmentId] : [],
                  sequences: [sequence],
                } as unknown as Partial<TelemonitoringRequestComponent>,
              })
              .subscribe();
          });
      },
    },
    {
      id: MenuActionId.SEQUENCE_UNLINK,
      label: this.translocoService.translate('Retirer'),
      icon: '@tui.minus',
      status: Status.ERROR,
      context: SequenceContext.TELEMONITORING,
      condition$: (sequence: FlattenedSequence): Observable<boolean> =>
        isEverythingTrue$(
          this.isPatientAnonymous(sequence) || this.isSequenceActive(sequence),
          this.isUserTelemonitoringOrSequenceOwner(sequence),
        ),
      execute: (sequence: FlattenedSequence): void => {
        this.dialogService
          .openConfirm(
            this.translocoService.translate('Êtes-vous certain de vouloir retirer cette séquence de ce télésuivi ?'),
          )
          .subscribe((result) => {
            if (result) {
              this.store.dispatch(
                telemonitoringsActions.unlinkSequence({
                  telemonitoringId: sequence.sequenceInstance.telemonitoringId,
                  clientCode: sequence.clientCode,
                  sequenceId: sequence.sequenceInstance.id,
                }),
              );
            }
          });
      },
    },
    {
      id: MenuActionId.SEQUENCE_DELETE,
      label: this.translocoService.translate('Supprimer'),
      icon: '@tui.trash',
      status: Status.ERROR,
      context: SequenceContext.SEQUENCE,
      condition$: (sequence: FlattenedSequence): Observable<boolean> =>
        isEverythingTrue$(
          this.isPatientAnonymous(sequence) || this.isSequenceActive(sequence),
          this.isUserAdminOrOwner$(sequence),
          this.isSequenceByUser(sequence),
        ),
      execute: (sequence: FlattenedSequence): void => {
        this.dialogService
          .openConfirm(this.translocoService.translate('Êtes-vous certain de vouloir supprimer cette séquence ?'), {
            content:
              this.translocoService.translate('Cette action est définitive. ') +
              this.translocoService.translate(
                'Si votre séquence appartient à plusieurs groupes, celle-ci sera supprimée dans tous les groupes.',
              ),
            yes: this.translocoService.translate('Supprimer'),
            isDestructive: true,
          })
          .subscribe((result) => {
            if (result) {
              this.store.dispatch(
                sequencesActions.deleteSequence({
                  sequenceId: sequence.sequenceInstance.id,
                  clientCode: sequence.clientCode,
                }),
              );
            }
          });
      },
    },
  ];

  getActions(context: SequenceContext = SequenceContext.SEQUENCE): MenuAction<FlattenedSequence>[] {
    return this.actions.filter((action) => !action.context || action.context === context);
  }

  addSequence(inputs: SequenceCreateInputs): Observable<boolean> {
    const { departmentIds, clientCode } = inputs;

    const departmentIds$ = departmentIds ? of(departmentIds) : this.getUserDepartmentIds$(clientCode);

    return departmentIds$.pipe(
      first(),
      switchMap((dptIds) =>
        this.dialogService.openComponentWithCloseConfirmation<SequenceCreateComponent, boolean>(
          SequenceCreateComponent,
          {
            label: this.translocoService.translate('Créer une séquence'),
            size: 'l',
            appearance: 'fullscreen',
            data: {
              ...inputs,
              departmentIds: dptIds,
            },
          },
        ),
      ),
    );
  }

  isUserOwner(sequence: FlattenedSequence): boolean {
    return (
      this.authorizationsService.userId === sequence.sequenceInstance.createdBy ||
      this.authorizationsService.userId === sequence.sequenceInstance.ownedBy
    );
  }

  isUserAdminOrOwner$(sequence: FlattenedSequence): Observable<boolean> {
    if (this.isUserOwner(sequence)) {
      return of(true);
    }
    return this.authorizationsService.isCurrentDepartmentAdmin$();
  }

  getUserDepartmentIds$(clientCode: string): Observable<number[]> {
    return this.store.select(selectUserEnabledClientDepartmentIds(clientCode, this.authorizationsService.userId));
  }

  isSequenceActive(sequence: FlattenedSequence): boolean {
    return (
      sequence.sequenceInstance &&
      !sequence.sequenceInstance.isArchived &&
      (this.isPatientAnonymous(sequence) || this.isSequencePatientActive(sequence))
    );
  }

  isPatientAnonymous({ patientInstance }: FlattenedSequence): boolean {
    return !patientInstance;
  }

  isSequencePatientActive({ patientInstance }: FlattenedSequence): boolean {
    return patientInstance && !patientInstance.isArchived;
  }

  isSequenceByUser(sequence: FlattenedSequence): boolean {
    return sequence.sequenceInstance.creatorType === CreatorType.BY_USER;
  }

  hasMultipleClients$(): Observable<boolean> {
    return this.store.select(selectClients).pipe(map((clients) => Object.keys(clients).length > 1));
  }

  isUserTelemonitoringOrSequenceOwner({ sequenceInstance }: FlattenedSequence): Observable<boolean> {
    return this.store.select(selectTelemonitoring(sequenceInstance.telemonitoringId)).pipe(
      map((telemonitoring) => {
        const userId = this.authorizationsService.userId;
        const isTelemonitoringOwner = userId === telemonitoring.telemonitoring.createdBy;
        const isSequenceOwner = userId === sequenceInstance.createdBy;
        return isTelemonitoringOwner || isSequenceOwner;
      }),
    );
  }
}
